import React, { useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import PropType from 'prop-types';
import { ReactComponent as Close } from '../../assets/images/svg/close.svg';
import themeColors from '../../assets/styles/themeColors';
import { mediaBelow, pxToRem, pxToRemMd } from '../../assets/styles/helper';
import { variables } from '../../assets/styles/variables';

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;
const fadeOut = keyframes`
  from { opacity: 1; }
  to { opacity: 0; }
`;

const slideUp = keyframes`
  from { transform: translateY(100%); }
  to { transform: translateY(0); }
`;
const slideDown = keyframes`
  from { transform: translateY(0); }
  to { transform: translateY(100%); }
`;

const Overlay = styled.div`
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  z-index: 40;
  animation: ${({ $closing }) => ($closing ? fadeOut : fadeIn)} 0.3s forwards;
`;

const Drawer = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: ${themeColors.colorWhite};
  border-radius: 10px 10px 0 0;
  box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.1);
  box-shadow: 0 54px 55px 0 rgba(0, 0, 0, 0.25),
    0 -12px 30px 0 rgba(0, 0, 0, 0.12), 0 4px 6px 0 rgba(0, 0, 0, 0.12),
    0 12px 13px 0 rgba(0, 0, 0, 0.17), 0 -3px 5px 0 rgba(0, 0, 0, 0.09);
  z-index: 50;
  max-height: 60%;
  display: flex;
  flex-direction: column;
  animation: ${({ $closing }) => ($closing ? slideDown : slideUp)} 0.3s forwards;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: ${({ hideClose }) => (!hideClose ? pxToRem(16) : pxToRem(4))};

  ${mediaBelow(variables.breakpoints.bpMd)} {
    font-size: ${pxToRemMd(14)};
  }
`;

const TitleWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const Title = styled.h2`
  font-size: ${pxToRem(16)};
  font-weight: 600;
  margin: 2px;
  color: ${themeColors.colorTextPrimary};

  ${mediaBelow(variables.breakpoints.bpMd)} {
    font-size: ${pxToRemMd(14)};
  }
`;

const Subtitle = styled.p`
  font-size: ${pxToRem(14)};
  font-weight: 400;
  color: ${themeColors.colorTextSecondary};
  margin: 4px 0 0;
`;

const CloseButton = styled.button`
  border: none;
  background: transparent;
  cursor: pointer;

  > svg {
    width: 20px;
    color: ${themeColors.colorTextPrimary};
  }
`;

const Content = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: ${pxToRem(8)} ${pxToRem(16)} ${pxToRem(16)};
`;

const Footer = styled.div`
  padding: 16px;

  button {
    font-size: ${pxToRem(16)};
    height: 44px;
  }
`;

export const DrawerButtonsWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-gap: ${pxToRem(12)};

  button > svg {
    width: ${pxToRem(20)};
    margin-left: ${pxToRem(15)};
  }

  ${mediaBelow(variables.breakpoints.bpMd)} {
    grid-template-columns: 1fr;
  }
`;

const BottomDrawer = ({
  isOpen,
  onClose,
  title,
  subtitle,
  children,
  footer,
  hideClose,
}) => {
  const [closing, setClosing] = React.useState(false);

  useEffect(() => {
    if (!isOpen) setClosing(false);
  }, [isOpen]);

  if (!isOpen && !closing) return null;

  const handleClose = () => {
    setClosing(true);
    setTimeout(() => {
      setClosing(false);
      onClose();
    }, 300);
  };

  return (
    <>
      {isOpen && (
        <>
          <Overlay $closing={closing} />
          <Drawer $closing={closing}>
            <Header hideClose={hideClose}>
              <TitleWrapper>
                <Title>{title}</Title>
                {subtitle && <Subtitle>{subtitle}</Subtitle>}
              </TitleWrapper>
              {!hideClose && (
                <CloseButton onClick={handleClose}>
                  <Close />
                </CloseButton>
              )}
            </Header>

            <Content>{children}</Content>

            {footer && <Footer>{footer}</Footer>}
          </Drawer>
        </>
      )}
    </>
  );
};

BottomDrawer.propTypes = {
  isOpen: PropType.bool,
  onClose: PropType.func,
  title: PropType.string,
  subtitle: PropType.string,
  children: PropType.node,
  footer: PropType.node,
  hideClose: PropType.bool,
};

export default BottomDrawer;
